import { useMemo, useRef } from "react"
import { useField } from "formik"

import { makeError } from "./validationUtils"

/**
 * This hook is a wrapper around Formik's useField hook.
 * useField returns a tuple containing data and methods to be passed down to html fields.
 * The return value from useField changes everytime an input in the form changes. If the value
 * of one input in the form changes, every input in the form is going to be re-rendered.
 * useFastField addresses some performance concerns caused by that by using useRef to store
 * references to the methods returned by useField and updating them only when strictly necessary.
 * @param {string | object} props The field name as a string or an object with a "name" property
 * @return {object} An object containing "field" and "helpers" from useField and "error", which is
 * a tRetailWebApp error object returned by validationUtils.
 */
export default function useFastField(props) {
  const [field, { error, touched }, helpers] = useField(props)
  const fieldRef = useFieldRef(field)
  const helpersRef = useHelpersRef(helpers)
  const errorObject = useMemo(() => makeError(error, touched), [error, touched])

  return {
    touched,
    field: fieldRef.current,
    error: errorObject,
    helpers: helpersRef.current.helpers
  }
}

function useFieldRef(field) {
  const newestFieldRef = useRef()
  newestFieldRef.current = field

  const fieldFactory = ({ name, value }) => ({
    name,
    value,
    onBlur: args => newestFieldRef.current.onBlur(args),
    onChange: args => newestFieldRef.current.onChange(args)
  })

  const fieldRef = useRef(fieldFactory(field))

  if (fieldRef.current.value !== field.value || fieldRef.current.name !== field.name) {
    fieldRef.current = fieldFactory(field)
  }

  return fieldRef
}

function useHelpersRef(helpers) {
  const helpersRef = useRef({})

  helpersRef.current.setValue = helpers.setValue
  helpersRef.current.setTouched = helpers.setTouched
  helpersRef.current.setError = helpers.setError

  if (!helpersRef.current.helpers) {
    helpersRef.current.helpers = {
      setValue: (...args) => helpersRef.current.setValue(...args),
      setTouched: (...args) => helpersRef.current.setTouched(...args),
      setError: (...args) => helpersRef.current.setError(...args)
    }
  }

  return helpersRef
}
